import { Controller } from "@hotwired/stimulus";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

export default class extends Controller {
  static targets = ["button", "menu", "template"];
  static classes = ["flip"];
  static values = {
    autoClose: { type: Boolean, default: true }, // Whether to close the menu when clicking on an item
    nested: { type: Boolean, default: false }, // Whether the menu is nested
    hover: { type: Boolean, default: false }, // Whether to show the menu on hover
    autoPosition: { type: Boolean, default: true }, // Whether to automatically position the menu
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the menu content
    placement: { type: String, default: "bottom-start" }, // The placement(s) of the menu - can be multiple separated by spaces
    dialogMode: { type: Boolean, default: true }, // Whether to use dialog mode
    turboFrameSrc: { type: String, default: "" }, // URL for Turbo Frame lazy loading
    lockScroll: { type: Boolean, default: false }, // Whether to lock scroll when menu is open
  };

  connect() {
    // Initialize scroll lock state
    this.isScrollLocked = false;

    // Initialize non-dialog menu if dialogMode is false
    if (!this.dialogModeValue && !this.menuTarget.hasAttribute("role")) {
      this.menuTarget.setAttribute("role", "menu");
      this.menuTarget.setAttribute("aria-modal", "false");
      this.menuTarget.setAttribute("tabindex", "-1");
    }

    this.menuTarget.addEventListener("menu-item-clicked", () => {
      if (this.autoCloseValue) {
        this.close();
        let parent = this.element.closest('[data-controller="dropdown-popover"]');
        while (parent) {
          const parentController = this.application.getControllerForElementAndIdentifier(parent, "dropdown-popover");
          if (parentController && parentController.autoCloseValue) {
            parentController.close();
          }
          parent = parent.parentElement.closest('[data-controller="dropdown-popover"]');
        }
      }
    });

    // Store bound handlers so disconnect() can actually remove them.
    // Anonymous functions or repeated .bind() calls create new references
    // each time, making removeEventListener a silent no-op (listener leak).
    this.boundDocumentKeydown = (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener("keydown", this.boundDocumentKeydown);

    // Add scroll listener to update position
    this.scrollHandler = () => {
      if (this.isOpen && this.autoPositionValue) {
        this.#updatePosition();
      }
    };
    window.addEventListener("scroll", this.scrollHandler, true);

    // Update position when window is resized or menu content changes
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isOpen) {
        this.#updatePosition();
      }
    });

    // Observe both document.body and the menuTarget element
    this.resizeObserver.observe(document.body);
    this.resizeObserver.observe(this.menuTarget);

    // Add MutationObserver to detect content changes inside the menu
    this.mutationObserver = new MutationObserver(() => {
      if (this.isOpen) {
        // Slight delay to allow DOM changes to complete
        setTimeout(() => this.#updatePosition(), 10);
      }
    });

    this.mutationObserver.observe(this.menuTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Add hover functionality for hover-enabled dropdowns
    if (this.hoverValue) {
      let hoverTimeout;

      // Track if mouse is over the dropdown area
      let isMouseOverDropdown = false;

      this.buttonTarget.addEventListener("mouseenter", () => {
        isMouseOverDropdown = true;
        clearTimeout(hoverTimeout);
        this.show();
      });

      this.buttonTarget.addEventListener("mouseleave", () => {
        isMouseOverDropdown = false;
        // Check if mouse is not over the menu
        hoverTimeout = setTimeout(() => {
          if (!isMouseOverDropdown && this.isOpen) {
            this.close();
          }
        }, 50); // Very short delay to allow moving to menu
      });

      this.menuTarget.addEventListener("mouseenter", () => {
        isMouseOverDropdown = true;
        clearTimeout(hoverTimeout);
      });

      this.menuTarget.addEventListener("mouseleave", () => {
        isMouseOverDropdown = false;
        hoverTimeout = setTimeout(() => {
          if (!isMouseOverDropdown && this.isOpen) {
            this.close();
          }
        }, 50); // Very short delay for smooth experience
      });

      // Keep the existing element mouseleave for nested menu handling
      this.element.addEventListener("mouseleave", (event) => {
        const toElement = event.relatedTarget;
        const isMovingToNestedMenu =
          toElement &&
          (toElement.closest('[data-dropdown-popover-target="menu"]') ||
            toElement.closest('[data-dropdown-popover-target="button"]') ||
            // Check if moving to a child menu
            toElement.closest('[data-controller="dropdown-popover"][data-dropdown-popover-nested-value="true"]'));

        if (!isMovingToNestedMenu) {
          hoverTimeout = setTimeout(() => {
            // Check if any child menu is being hovered before closing
            const hasHoveredChild = Array.from(
              this.menuTarget.querySelectorAll('[data-controller="dropdown-popover"]')
            ).some(
              (dropdown) =>
                dropdown.matches(":hover") ||
                dropdown.querySelector("[data-dropdown-popover-target='menu']").matches(":hover")
            );

            if (!hasHoveredChild) {
              this.close();

              // Reset focus state when menu is closed with hover
              if (this.nestedValue) {
                this.buttonTarget.classList.remove("active-menu-path");
                this.buttonTarget.classList.remove("!bg-neutral-400/20");
              }
            }
          }, 100); // Reduced from 200ms to 100ms for more responsive closing
        }
      });
    } else {
      // For non-hover dropdowns, close any open hover dropdowns when hovering the button
      this.buttonTarget.addEventListener("mouseenter", () => {
        const allHoverDropdowns = this.application.controllers.filter(
          (c) => c.identifier === "dropdown-popover" && c !== this && c.isOpen && c.hoverValue
        );

        allHoverDropdowns.forEach((controller) => {
          controller.close();
        });
      });
    }
  }

  disconnect() {
    // Make sure to unlock scroll when controller disconnects
    if (this.isScrollLocked) {
      this.unlockScroll();
    }
    this.resizeObserver.disconnect();
    // Disconnect mutation observer
    this.mutationObserver.disconnect();
    // Remove scroll listener
    window.removeEventListener("scroll", this.scrollHandler, true);
    // Remove the stored document keydown listener.
    document.removeEventListener("keydown", this.boundDocumentKeydown);
  }

  get isOpen() {
    if (this.dialogModeValue) {
      return this.menuTarget.open;
    } else {
      return this.menuTarget.classList.contains("hidden") === false;
    }
  }

  async show() {
    // Close all other open dropdowns that aren't in the same hierarchy
    const allDropdowns = this.application.controllers.filter(
      (c) => c.identifier === "dropdown-popover" && c !== this && c.isOpen
    );

    allDropdowns.forEach((controller) => {
      if (!this.element.contains(controller.element) && !controller.element.contains(this.element)) {
        controller.close();
      }
    });

    // If we have lazy loading enabled, load the content now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    // Lock scroll if enabled
    if (this.lockScrollValue) {
      this.lockScroll();
    }

    if (this.dialogModeValue) {
      this.menuTarget.show();
    } else {
      this.menuTarget.classList.remove("hidden");
    }

    this.#updateExpanded();
    this.#updatePosition();

    // Add active-menu-path class and background classes to the button when showing the dropdown
    if (this.nestedValue) {
      this.buttonTarget.classList.add("active-menu-path");
      this.buttonTarget.classList.add("!bg-neutral-400/20");
    }

    requestAnimationFrame(() => {
      // Add appropriate classes based on placement
      if (this.placementValue.startsWith("top")) {
        this.menuTarget.classList.add("[&[open]]:scale-100", "[&[open]]:opacity-100", "scale-100", "opacity-100");
      } else {
        this.menuTarget.classList.add("[&[open]]:scale-100", "[&[open]]:opacity-100", "scale-100", "opacity-100");
      }

      // Check for autofocus elements inside the menu
      const autofocusElement = this.menuTarget.querySelector('[autofocus="true"], [autofocus]');
      if (autofocusElement) {
        // Focus the autofocus element
        setTimeout(() => {
          autofocusElement.focus();

          // If it's an input or textarea, position cursor at the end
          if (autofocusElement.tagName === "INPUT" || autofocusElement.tagName === "TEXTAREA") {
            const length = autofocusElement.value.length;
            autofocusElement.setSelectionRange(length, length);
          }
        }, 0);
      } else if (this.nestedValue) {
        this.menuTarget.focus();
      } else {
        this.buttonTarget.focus();
      }
    });
  }

  close() {
    // Unlock scroll if it was locked
    if (this.isScrollLocked) {
      this.unlockScroll();
    }

    // Close any child dropdowns first
    const childDropdowns = this.menuTarget.querySelectorAll('[data-controller="dropdown-popover"]');
    childDropdowns.forEach((dropdown) => {
      const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
      if (controller && controller.isOpen) {
        controller.close();
      }
    });

    // Remove all active-menu-path classes within this menu
    this.menuTarget.querySelectorAll(".active-menu-path").forEach((el) => {
      el.classList.remove("active-menu-path");
    });
    // Also remove from the button that triggered this dropdown and remove background classes
    this.buttonTarget.classList.remove("active-menu-path");
    this.buttonTarget.classList.remove("!bg-neutral-400/20");

    // If this is a hover-enabled dropdown, ensure we blur any focused elements
    if (this.hoverValue) {
      const focusedElement = this.element.querySelector(":focus");
      if (focusedElement) {
        focusedElement.blur();
      }
    }

    this.menuTarget.classList.remove("scale-100", "opacity-100", "[&[open]]:scale-100", "[&[open]]:opacity-100");
    setTimeout(() => {
      if (this.dialogModeValue) {
        this.menuTarget.close();
      } else {
        this.menuTarget.classList.add("hidden");
      }
      this.#updateExpanded();
    }, 100);
  }

  toggle() {
    this.isOpen ? this.close() : this.show();
  }

  closeOnClickOutside({ target }) {
    const isClickInNestedDropdown = target.closest('[data-dropdown-popover-nested-value="true"]');
    if (isClickInNestedDropdown) return;

    if (!this.element.contains(target)) {
      this.close();
    }
  }

  // Prevent close method to stop click propagation
  preventClose(event) {
    // Stop propagation to prevent the closeOnClickOutside handler from being triggered
    event.stopPropagation();
  }

  // Reset method to handle dialog close events
  reset() {
    // This method is called when the dialog is closed
    // It's responsible for any cleanup needed after closing

    // Dispatch a custom event that can be listened for by other controllers
    const resetEvent = new CustomEvent("dropdown-reset", {
      bubbles: true,
      detail: { controller: this },
    });
    this.element.dispatchEvent(resetEvent);
  }

  #updateExpanded() {
    this.buttonTarget.ariaExpanded = this.isOpen;
  }

  async #loadTemplateContent() {
    // Find the container in the menu to append content to
    const container = this.menuTarget.querySelector("[data-dropdown-popover-content]") || this.menuTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "dropdown-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading (existing behavior)
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }

  async #updatePosition() {
    // Parse placement value to support multiple placements
    const placements = this.placementValue.split(/[\s,]+/).filter(Boolean);
    const primaryPlacement = placements[0] || "bottom-start";
    const fallbackPlacements = placements.slice(1);

    const middleware = [
      offset(this.nestedValue ? -4 : 4), // 0 offset for nested, 4px for regular
      flip({
        fallbackPlacements: fallbackPlacements.length > 0 ? fallbackPlacements : ["top-start", "bottom-start"],
      }),
      shift({ padding: 8 }), // Keep 8px padding from window edges
    ];

    if (this.nestedValue) {
      // For nested dropdowns, position to the right of the button
      const { x, y } = await computePosition(this.buttonTarget, this.menuTarget, {
        placement: "right-start",
        middleware,
      });

      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    } else {
      // Use the primary placement from the controller
      const { x, y } = await computePosition(this.buttonTarget, this.menuTarget, {
        placement: primaryPlacement,
        middleware: this.autoPositionValue ? middleware : [offset(this.nestedValue ? -4 : 4)],
      });

      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    }
  }

  // Add scroll locking methods
  lockScroll() {
    if (this.isScrollLocked) return;

    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Store current body styles
    this.previousBodyPosition = document.body.style.position;
    this.previousBodyTop = document.body.style.top;
    this.previousBodyWidth = document.body.style.width;

    // Lock scroll by fixing the body position
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = "100%";

    this.isScrollLocked = true;
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;

    // Restore previous body styles
    document.body.style.position = this.previousBodyPosition || "";
    document.body.style.top = this.previousBodyTop || "";
    document.body.style.width = this.previousBodyWidth || "";

    // Restore scroll position
    window.scrollTo(0, this.scrollPosition);

    this.isScrollLocked = false;
  }
}
